/**
 * SDK datebar 共享层(三元素共用)
 *
 * 抽出 <hotel-date-bar> / <hotel-book-bar> / <hotel-package-list> 的公共逻辑:
 * 酒店解析(授权门禁 + loading/denied)、日期状态(URL>缓存>默认)、外部 setDate 同步、
 * datechange 派发、writeCachedDates、window.open 跳转。消除原 mode 分支。
 *
 * 日期优先级:check-in/check-out 属性 > readCachedDates > getDefaultDates(镜像 PackagesClient)。
 * 事件 composed:true 穿 Shadow DOM;window.open 在用户手势同步栈(防拦截)。
 */
import { useEffect, useState } from 'react';
import { resolveHotel, getDomain } from '../api/hotel';
import { getConfig } from '../config';
import { readCachedDates, writeCachedDates, getDefaultDates } from '@/lib/shared-storage';
import { toLocalDateString, parseDate, startOfDay } from '../lib/date';
import type { DateChangePayload, HotelErrorPayload } from '../types';

export type HotelState = 'loading' | 'ok' | 'denied';

/** 三元素 App 的公共 props */
export interface AppProps {
  /** 宿主自定义元素(事件派发目标) */
  host: HTMLElement;
  /** 初始入住日期 yyyy-MM-dd(经 check-in 属性) */
  initialCheckIn?: string;
  /** 初始离店日期 yyyy-MM-dd(经 check-out 属性) */
  initialCheckOut?: string;
  /** 内联列表最多显示的房型数量(仅 <hotel-package-list> 生效);
   *  正整数生效,不传/非正整数=默认 3 个。date-bar/book-bar 忽略此字段。 */
  limit?: number;
}

/** 镜像 PackagesClient.resolveInitialDates:URL > 缓存 > 默认 */
export function resolveInitialDates(
  urlCi?: string,
  urlCo?: string
): { checkIn: Date; checkOut: Date } {
  if (urlCi && urlCo) {
    const ci = parseDate(urlCi);
    const co = parseDate(urlCo);
    if (ci && co && co.getTime() > ci.getTime()) {
      return { checkIn: startOfDay(ci), checkOut: startOfDay(co) };
    }
  }
  const cached = readCachedDates();
  if (cached) return cached;
  return getDefaultDates();
}

/** 在宿主元素上派发穿 Shadow DOM 事件 */
export function dispatch(host: HTMLElement, type: string, detail: unknown): void {
  host.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
}

/** 同步手势栈内 window.open 跳转(basePath 前缀 + query) */
export function openUrl(path: string, params: Record<string, string>): void {
  const { basePath } = getConfig();
  const qs = new URLSearchParams(params).toString();
  // 同步手势栈内调用 window.open,避免被浏览器拦截
  window.open(`${basePath}${path}?${qs}`, '_blank');
}

export interface DateBarController {
  hotel: HotelState;
  dates: { checkIn: Date; checkOut: Date };
  handleDateChange: (checkIn: Date, checkOut: Date) => void;
}

/**
 * 三元素共用的日期栏控制器:酒店解析 + 日期状态 + 外部 setDate 同步 + datechange 派发。
 *
 * - 酒店解析:resolveHotel 模块级去重 + 缓存,多实例共享一次 fetch
 * - 外部 setDate(经 check-in/check-out 属性)同步内部状态用"adjust state during render"
 *   (条件 setState,React 允许)替代 effect,避免 react-hooks/set-state-in-effect 级联渲染;
 *   用户日历选择不回写属性,故不冲突
 */
export function useDateBarController(
  host: HTMLElement,
  initialCheckIn?: string,
  initialCheckOut?: string
): DateBarController {
  const [hotel, setHotel] = useState<HotelState>('loading');
  const [dates, setDates] = useState(() => resolveInitialDates(initialCheckIn, initialCheckOut));
  // 记录上次属性值,用于"adjust state during render"同步外部 setDate
  const [lastCheckIn, setLastCheckIn] = useState(initialCheckIn);
  const [lastCheckOut, setLastCheckOut] = useState(initialCheckOut);

  // 解析酒店(模块级去重 + 缓存,多实例共享一次 fetch)
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const info = await resolveHotel();
      if (!cancelled) setHotel(info ? 'ok' : 'denied');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 酒店状态联动宿主元素显隐 + 失败派发事件:
  //  - loading/denied:宿主挂 hidden(复用 :host([hidden]){display:none}),彻底不占位、无任何占位 UI
  //  - ok:摘 hidden,渲染真实内容
  //  - denied:额外派发 hotelerror(composed 穿 Shadow DOM),接入方可监听兜底
  // loading 期间 display:none 不影响 React effect/resolveHotel 执行,ok 后摘 hidden 无死锁。
  useEffect(() => {
    if (hotel === 'ok') {
      host.removeAttribute('hidden');
    } else {
      host.setAttribute('hidden', '');
    }
    if (hotel === 'denied') {
      dispatch(host, 'hotelerror', {
        domain: getDomain() || undefined,
      } satisfies HotelErrorPayload);
    }
  }, [hotel, host]);

  // 外部 setDate 同步(见上注释)
  if (initialCheckIn !== lastCheckIn || initialCheckOut !== lastCheckOut) {
    setLastCheckIn(initialCheckIn);
    setLastCheckOut(initialCheckOut);
    const ci = parseDate(initialCheckIn ?? '');
    const co = parseDate(initialCheckOut ?? '');
    if (ci && co && co.getTime() > ci.getTime()) {
      setDates({ checkIn: startOfDay(ci), checkOut: startOfDay(co) });
    }
  }

  const handleDateChange = (checkIn: Date, checkOut: Date) => {
    setDates({ checkIn, checkOut });
    writeCachedDates(checkIn, checkOut);
    const payload: DateChangePayload = {
      checkIn: toLocalDateString(checkIn),
      checkOut: toLocalDateString(checkOut),
    };
    dispatch(host, 'datechange', payload);
  };

  return { hotel, dates, handleDateChange };
}
